import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Application } from '@feathersjs/feathers';
import { AddDistrictTypeModal } from '../../components';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import {
  MeasureList,
  MeasureScoutingConfigurationEntry
} from '../../app/feathers/measure-lists';
import { Election } from '../../app/feathers/elections/Election';
import { District } from '../../app/feathers/districts/District';

export interface MeasureScoutingStepContentProps {
  measureList: MeasureList;
  election: Election | null;
  electionKey: string;
  feathers: Application<ServiceTypes>;
  onConfirmScouting: () => Promise<void>;
  onMeasureListUpdated: (list: MeasureList) => void;
  /** District types that support measures (from state config); only these can be added */
  allowTypes?: string[];
}

export const MeasureScoutingStepContent = ({
  measureList,
  election,
  electionKey,
  feathers,
  onConfirmScouting,
  onMeasureListUpdated,
  allowTypes = []
}: MeasureScoutingStepContentProps) => {
  const [savingScoutingConfiguration, setSavingScoutingConfiguration] = useState(false);
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration | null>(null);
  const [markedDistricts, setMarkedDistricts] = useState<District[]>([]);
  const [loadingMarkedDistricts, setLoadingMarkedDistricts] = useState(false);
  const stateName = election?.stateName ?? '';
  const markedDistrictIdsKey = (measureList.markedDistricts || []).join('|');

  useEffect(() => {
    if (!feathers || !election?.stateCode) return;
    feathers
      .getService('state-configurations')
      .get((election.stateCode || '').toLowerCase())
      .then((config: StateConfiguration) => setStateConfiguration(config))
      .catch(() => {});
  }, [feathers, election?.stateCode]);

  useEffect(() => {
    const markedDistrictIds = markedDistrictIdsKey ? markedDistrictIdsKey.split('|') : [];
    if (!feathers || markedDistrictIds.length === 0) {
      setMarkedDistricts([]);
      return;
    }

    let active = true;
    const loadMarkedDistricts = async () => {
      setLoadingMarkedDistricts(true);
      try {
        const chunkSize = 20;
        const responses = await Promise.all(
          Array.from(
            { length: Math.ceil(markedDistrictIds.length / chunkSize) },
            (_, index) => markedDistrictIds.slice(index * chunkSize, (index + 1) * chunkSize)
          ).map(ids => feathers.getService('districts').find({
            query: { _id: ids, $limit: chunkSize }
          }))
        );
        const districtsById = new Map(
          responses.flatMap(response => response.data).map(district => [district._id, district])
        );
        if (active) {
          setMarkedDistricts(
            markedDistrictIds
              .map(id => districtsById.get(id))
              .filter((district): district is District => Boolean(district))
          );
        }
      } catch (err) {
        console.error('Error loading marked districts:', err);
        if (active) setMarkedDistricts([]);
      } finally {
        if (active) setLoadingMarkedDistricts(false);
      }
    };

    loadMarkedDistricts();
    return () => {
      active = false;
    };
  }, [feathers, markedDistrictIdsKey]);

  const handleSaveScoutingConfiguration = async (newConfig: MeasureScoutingConfigurationEntry[]) => {
    if (!measureList?._id || !feathers) return;
    setSavingScoutingConfiguration(true);
    try {
      const response = await feathers
        .getService('measure-lists')
        .patch(measureList._id, { scoutingConfiguration: newConfig });
      onMeasureListUpdated(response);
    } catch (err) {
      console.error('Error saving scouting configuration:', err);
    } finally {
      setSavingScoutingConfiguration(false);
    }
  };

  const scoutingConfig = measureList.scoutingConfiguration || [];

  return (
    <>
      <Typography variant="body1" style={{ marginBottom: '16px', color: '#666' }}>
        Determine which district types under the state should be marked as complete for measures when creating this list.
        The state district is always included and marked complete. You can add other measure-supporting district types below, or click Confirm Scouting to continue with only the state marked.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '16px' }}>
        <InfoOutlinedIcon style={{ fontSize: 16, cursor: 'help' }} />
        <Typography variant="body2" color="textPrimary">
          Defaults can be loaded from the state configuration, which you can edit{' '}
          <Link
            to={`/state-election-configurations/${(stateName || '').replace(/\s+/g, '-').toLowerCase()}`}
            style={{ textDecoration: 'underline' }}
          >
            here
          </Link>
          .
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, marginBottom: 2 }}>
        {scoutingConfig.map((config, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" style={{ minWidth: '120px', fontWeight: 500 }}>
              {humanizeDistrictType(config.districtType, stateConfiguration?.typeConfigurations ?? null)}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={config.coverageExtent}
                onChange={(e) => {
                  const newConfig = [...scoutingConfig];
                  newConfig[index] = {
                    ...newConfig[index],
                    coverageExtent: e.target.value as 'full-coverage' | 'limited-coverage' | 'no-coverage'
                  };
                  handleSaveScoutingConfiguration(newConfig);
                }}
                disabled={savingScoutingConfiguration}
                sx={{ backgroundColor: 'white', '& .MuiSelect-select': { padding: '8px 32px 8px 12px' } }}
              >
                <MenuItem value="full-coverage">Mark all as complete</MenuItem>
                <MenuItem value="limited-coverage">Mark only those in the content</MenuItem>
                <MenuItem value="no-coverage">Mark none</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>
      {stateName && (
        <AddDistrictTypeModal
          stateName={stateName}
          existingTypes={scoutingConfig.map((c) => c.districtType)}
          excludeTypes={scoutingConfig.map((c) => c.districtType)}
          restrictToTiers={[1, 2]}
          allowTypes={allowTypes.length > 0 ? allowTypes : undefined}
          onSelect={(districtType) => {
            if (!scoutingConfig.some((c) => c.districtType === districtType)) {
              handleSaveScoutingConfiguration([
                ...scoutingConfig,
                { districtType, coverageExtent: 'limited-coverage' }
              ]);
            }
          }}
          buttonComponent={({ onClick }) => (
            <Button
              variant="outlined"
              size="small"
              onClick={onClick}
              disabled={savingScoutingConfiguration}
            >
              Add district type
            </Button>
          )}
        />
      )}
      <Box sx={{ marginTop: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Source for implementation districts: {measureList.source || '—'}
        </Typography>
      </Box>
      <Divider style={{ margin: '24px 0 16px' }} />
      <Typography variant="h6" style={{ marginBottom: 8 }}>
        Districts marked complete ({measureList.markedDistricts?.length || 0})
      </Typography>
      {loadingMarkedDistricts ? (
        <Typography variant="body2" color="textSecondary">
          Loading districts…
        </Typography>
      ) : markedDistricts.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            maxHeight: 280,
            overflowY: 'auto',
            padding: 1.5,
            backgroundColor: '#f7f9fe',
            border: '1px solid #e0e0e0',
            borderRadius: 1
          }}
        >
          {markedDistricts.map(district => (
            <Box
              key={district._id}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
            >
              <Typography variant="body2">
                {district.longName || district.name || district.matchName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {humanizeDistrictType(
                  district.type,
                  stateConfiguration?.typeConfigurations ?? null
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          No districts are currently marked complete.
        </Typography>
      )}
      <Divider style={{ margin: '24px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onConfirmScouting}
          disabled={savingScoutingConfiguration}
          style={{ minWidth: '200px' }}
        >
          Confirm Scouting
        </Button>
      </div>
    </>
  );
};
